import { GENERIC_ERROR_MESSAGE } from "@/lib/errors/error-messages";
import { BffErrorCode } from "@/lib/http/bff-error-codes";
import { BffFailure, ClientHttpError } from "@/lib/http/client-http";

/**
 * What the person reads when a panel save fails (criteria 20.3 and 20.4).
 *
 * Two paths, never mixed: a business failure carries a stable `code` that the table below
 * translates, while a transport failure already carries the centralized message, which is
 * the one that helps ("check your connection") rather than an explanation of a rule.
 */

const HOST_SAVE_MESSAGES: Partial<Record<string, string>> = {
  [BffErrorCode.INVALID_INPUT]: "Confira os dados e tente de novo.",
  [BffErrorCode.RATE_LIMITED]:
    "Muitas tentativas em pouco tempo. Aguarde um instante e tente de novo.",
  [BffErrorCode.UNAUTHENTICATED]: "Sua sessão expirou. Entre novamente para salvar.",
  [BffErrorCode.FORBIDDEN]: "Esta conta não pode alterar esta lista.",
  [BffErrorCode.NOT_FOUND]: "Não encontramos a sua lista. Recarregue a página e tente de novo.",
  /*
   * Birth date and gender got their column in 2026-10-07 (PR #56), so this no longer names
   * them: the code stays mapped for the next field the design asks for and the contract lacks,
   * and the copy says what is true of any such field instead of a list that went stale.
   */
  [BffErrorCode.PROFILE_FIELD_UNSUPPORTED]:
    "Um dos campos ainda não pode ser salvo. Edite os outros e nos avise para liberarmos esse.",
  [BffErrorCode.LIST_ITEM_HAS_PURCHASES]:
    "Essa peça já recebeu presentes: ela não pode sair da lista nem ficar com menos unidades do que já foi dado.",
  [BffErrorCode.SERVER_ERROR]: "Não foi possível salvar agora. Tente novamente em instantes.",
};

export const HOST_SAVE_FALLBACK = "Não foi possível salvar. Tente novamente.";

/**
 * A refusal the backend gives for **one piece** of a batch and then applies to all of it: the
 * piece already has gifts (409), or the delete hit a constraint the backend does not translate
 * (a piece sitting in someone's cart answers 500 — measured 2026-10-22). The transaction rolls
 * back whole, so the pieces that could have gone are refused with it.
 *
 * Only these two are worth isolating: anything else (a dead session, the rate limit, no
 * connection) would fail every piece the same way, and asking again for each would only multiply
 * the requests.
 */
export function isPerPieceRefusal(error: unknown): boolean {
  return (
    error instanceof BffFailure &&
    (error.code === BffErrorCode.LIST_ITEM_HAS_PURCHASES || error.code === BffErrorCode.SERVER_ERROR)
  );
}

/**
 * What the person reads for one piece that was refused, once the others were let through.
 *
 * The 500 is not worded as a cause: the frontend cannot tell a piece in a cart from a server
 * that is down, since both arrive as `SERVER_ERROR`. Saying "already gifted" there would assert
 * something that may be false, so it says only what is certain — that this piece did not leave.
 */
export function pieceRefusalMessage({
  error,
  isRemoval,
  name,
}: {
  error: unknown;
  isRemoval: boolean;
  name: string;
}): string {
  if (isRemoval && error instanceof BffFailure) {
    if (error.code === BffErrorCode.LIST_ITEM_HAS_PURCHASES) {
      return `${name} já recebeu presentes e não pode sair da lista.`;
    }
    if (error.code === BffErrorCode.SERVER_ERROR) {
      return `Não foi possível tirar ${name} da lista agora. Tente de novo em instantes.`;
    }
  }

  return hostSaveMessage(error);
}

export function hostSaveMessage(error: unknown): string {
  if (error instanceof BffFailure) {
    return HOST_SAVE_MESSAGES[error.code] ?? HOST_SAVE_FALLBACK;
  }

  if (error instanceof ClientHttpError) {
    return error.message;
  }

  // A programming error is not product copy.
  return GENERIC_ERROR_MESSAGE;
}
